/**
 * @file index.tsx
 *
 * @fileoverview Renders a long string drupal type field on
 * an entity as a textarea.
 */

import React, { useDeferredValue, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { WYSIWYGEditor } from '~/jobiqo/components/WYSIWYG';
import { EntityField } from '~/jobiqo/models/entity';
import { InlineError } from '~/vcl/atoms/01-forms/09-inline-error';
import { FormItem } from '~/vcl/atoms/01-forms/11-form-item';
import { FormLabel } from '~/vcl/atoms/01-forms/11-form-item/01-form-label';
import { FormDescription } from '~/vcl/atoms/01-forms/11-form-item/02-form-description';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { Text } from '~/vcl/atoms/13-text/03-text';
import { getPlaceholder, getSetting } from '../../helpers/formSettings';
import { getAllowedTextLength } from '../Form/hooks/helpers/validation';
import { EntityFieldRules } from '~/jobiqo/models/config';
import { useFieldRules } from '../../hooks/useFieldRules';

interface Props extends React.InputHTMLAttributes<HTMLTextAreaElement> {
  /**
   * The field definition.
   */
  entityField: EntityField;
  /**
   * The field rules to check if the field is required, hidden or disabled.
   */
  fieldRules?: EntityFieldRules;
}

/**
 * Gets the number of rows for the textarea based on form settings.
 *
 * @param {EntityField} field
 *   The field from which to read the number of rows from.
 *
 * @returns {number}
 *   The number of rows in the field settings.
 */
const getNumRows = (field: EntityField): number => {
  const setting = field.settings.filter((fieldSetting) => {
    return fieldSetting.key === 'form_settings';
  });
  if (setting.length > 0 && setting[0].value.rows) {
    return setting[0].value.rows;
  }
  return 10;
};

export default function TextLongWidget({
  entityField,
  fieldRules = {},
  ...props
}: Props) {
  const { setValue, getValues, control } = useFormContext();
  const values = getValues();
  const { hidden, required } = useFieldRules(
    entityField.id,
    values,
    fieldRules,
  );
  const rows = getNumRows(entityField);
  const allowedTextLength = getAllowedTextLength(entityField);
  const remainingCharactersUnlimited = allowedTextLength === 0;

  // When field is hidden reset field state. this needs to be done inside a hook
  // otherwise it will not work.
  useEffect(() => {
    if (hidden && !!values[entityField.id]) {
      setValue(entityField.id, null);
    }
  }, [hidden, values, entityField]);

  if (hidden) {
    return null;
  }

  return (
    <div id={entityField.id}>
      <Controller
        name={entityField.id}
        control={control}
        render={({ field, fieldState: { error } }) => {
          const fieldValueLength = useDeferredValue(field.value?.length ?? 0);

          let remainingCharacters = allowedTextLength - fieldValueLength;
          if (remainingCharacters < 0) {
            remainingCharacters = 0;
          }

          return (
            <>
              <FormItem>
                <FormLabel
                  required={entityField.required || required}
                  htmlFor={field.name}
                >
                  {entityField.label}
                </FormLabel>
                <WYSIWYGEditor
                  {...field}
                  {...props}
                  id={field.name}
                  placeholder={getPlaceholder(entityField)}
                  rows={rows}
                  onChange={(_, editor) => {
                    setValue(field.name, editor.getData(), {
                      shouldValidate: true,
                    });
                  }}
                  content={field.value}
                  maxLength={getSetting('max_length', entityField)}
                />
                <FlexItems
                  gap={10}
                  justifyContent="space-between"
                  className="vcl-full-width"
                >
                  {entityField.description && (
                    <FormDescription content={entityField.description} />
                  )}
                  {entityField.allowedFormatsMaxTextLength?.length > 0 &&
                    !remainingCharactersUnlimited && (
                      <Text
                        marginTop={4}
                        fontSize={2}
                        {...(remainingCharacters === 0 && {
                          color: 'danger',
                        })}
                      >
                        <FormattedMessage
                          id="number_of_characters_remaining"
                          defaultMessage="{remainingCharacters} characters remaining"
                          values={{
                            remainingCharacters,
                          }}
                        />
                      </Text>
                    )}
                </FlexItems>
              </FormItem>
              {!!error && <InlineError message={error.message} />}
            </>
          );
        }}
      />
    </div>
  );
}

export { TextLongWidget };
